import { useEffect, useState } from "react"
import { App } from "@capacitor/app"
import { VibratoClient } from "@/lib/vibrato-api"
import { settings } from "@/settings"
import mixpanel from "mixpanel-browser"

export default function useReloadOnNewPlan(client: VibratoClient) {
  if (!settings.IS_NATIVE_APP) {
    return
  }

  const [initialPlanSlug, setInitialPlanSlug] = useState<string | null>(null)

  useEffect(() => {
    if (!client) {
      return
    }

    let resumeListener: any = null

    // Fetch initial plan
    const fetchInitialPlan = async () => {
      try {
        const planData = await client.getPlan()
        setInitialPlanSlug(planData.plan_slug)
        console.log("App: initial plan slug set", planData.plan_slug)

        mixpanel.people.set({
          plan_slug: planData.plan_slug
        })
      } catch (error) {
        console.error("Failed to fetch initial plan", error)
      }
    }

    fetchInitialPlan()

    const setupResumeListener = async () => {
      // Setup app resume listener
      resumeListener = await App.addListener("resume", async () => {
        console.log("App: resumed in useRefreshToken")

        try {
          // First check if plan has changed
          const currentPlanData = await client.getPlan()
          const currentPlanSlug = currentPlanData.plan_slug

          console.log("App: comparing plan slugs", {
            initialPlanSlug,
            currentPlanSlug
          })

          if (initialPlanSlug && currentPlanSlug !== initialPlanSlug) {
            mixpanel.people.set({
              plan_slug: currentPlanSlug
            })

            if (!settings.ENABLE_RELOAD_ON_NEW_PLAN) {
              console.log("App: plan changed, but reload is disabled, not reloading")
              return
            }

            console.log("App: plan changed, reloading", {
              from: initialPlanSlug,
              to: currentPlanSlug
            })

            window.location.reload()
            return
          }

          // If plan hasn't changed, call getUserApiSession as a backup check
          await client.getUserApiSession()
          console.log("App: session valid, not reloading")
        } catch (error) {
          if (error instanceof TypeError || (error instanceof Error && error.message.includes("load failed"))) {
            console.log("App: TypeError or load failed occurred, not reloading", error)
            return
          }

          console.log("App: session invalid or error checking plan, reloading", error)
          window.location.reload()
        }
      })
    }

    setupResumeListener()

    return () => {
      // Cleanup app resume listener
      if (resumeListener) {
        resumeListener.remove()
        console.log("App: resume listener removed in useRefreshToken")
      }
    }
  }, [client, initialPlanSlug])
}
